import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Ghost, Maximize2, Minimize2, ExternalLink, Settings, Dog } from 'lucide-react';
import { db, auth } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, limit } from 'firebase/firestore';

interface Message {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  timestamp: any;
}

interface Props {
  onClose: () => void;
  isDetached?: boolean;
  onToggleDetach?: () => void;
}

export default function ChatRoom({ onClose, isDetached = false, onToggleDetach }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isGhost, setIsGhost] = useState(false);
  const [opacity, setOpacity] = useState(80);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'chat_messages'),
      orderBy('timestamp', 'asc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Message));
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !auth.currentUser) return;

    await addDoc(collection(db, 'chat_messages'), {
      text: newMessage,
      senderId: auth.currentUser.uid,
      senderName: auth.currentUser.displayName || 'Anonymous',
      timestamp: serverTimestamp()
    });
    setNewMessage('');
  };

  const handlePopOut = () => {
    const url = window.location.origin + window.location.pathname + `?mode=chat&isGhost=${isGhost}&opacity=${opacity}`;
    window.open(url, 'ChatRoom', 'width=450,height=600,menubar=no,toolbar=no,location=no,status=no');
  };

  const containerStyle = {
    backgroundColor: isGhost ? `rgba(28, 25, 23, ${opacity / 100})` : '#1c1917',
    borderColor: '#78716c',
    backdropFilter: isGhost ? 'blur(8px)' : 'none',
    opacity: isGhost ? opacity / 100 : 1
  };

  return (
    <div className={`fixed z-50 ${isDetached ? 'inset-0' : 'bottom-4 right-4 w-96 h-[500px]'} flex items-center justify-center p-4 font-mono`}>
      <div className="w-full h-full terminal-screen rounded-sm flex flex-col shadow-[0_0_15px_rgba(217,119,6,0.15)] overflow-hidden" style={containerStyle}>
        <div className="p-3 border-b border-amber-900/30 flex justify-between items-center bg-[#0c0a09]/80 relative z-10">
          <h2 className="text-sm font-bold text-amber-500 uppercase tracking-widest flex items-center gap-2">
            <Dog size={16} /> Noble Glitch & Arya Comms
          </h2>
          <div className="flex items-center gap-2">
            <input 
              type="range" 
              min="10" 
              max="100" 
              value={opacity} 
              onChange={(e) => setOpacity(Number(e.target.value))}
              className="w-16 h-1 bg-stone-900 rounded-sm appearance-none cursor-pointer accent-amber-600"
            />
            <button onClick={() => setIsGhost(!isGhost)} className={`p-1 rounded-sm ${isGhost ? 'text-amber-400 bg-amber-900/20' : 'text-amber-400 hover:text-amber-400'}`}>
              <Ghost size={14} />
            </button>
            <button onClick={handlePopOut} className="p-1 rounded-sm text-amber-400 hover:text-amber-400">
              <ExternalLink size={14} />
            </button>
            {onToggleDetach && (
              <button onClick={onToggleDetach} className="p-1 rounded-sm text-amber-400 hover:text-amber-400">
                {isDetached ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            )}
            <button onClick={onClose} className="p-1 rounded-sm text-amber-400 hover:text-amber-400"><X size={14} /></button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4 relative z-10">
          {messages.map(msg => (
            <div key={msg.id} className={`flex flex-col ${msg.senderId === auth.currentUser?.uid ? 'items-end' : 'items-start'}`}>
              <span className="text-[9px] text-amber-400 mb-1 uppercase tracking-widest">{msg.senderName}</span>
              <div className={`p-2 rounded-sm max-w-[80%] text-xs border ${msg.senderId === auth.currentUser?.uid ? 'bg-amber-900/30 text-amber-500 border-amber-700/50' : 'bg-stone-900/50 text-amber-600 border-stone-800'}`}>
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={sendMessage} className="p-3 border-t border-amber-900/30 flex gap-2 bg-[#0c0a09]/80 relative z-10">
          <input 
            type="text" 
            value={newMessage} 
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Transmit message..."
            className="flex-1 terminal-input p-2 rounded-sm text-xs"
          />
          <button type="submit" className="bg-amber-600 text-black p-2 rounded-sm hover:bg-amber-500 transition-colors">
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
